<template>
    <NuxtLayout name="casino">
        <ClientOnly>
            <GameSettingsDiceTabsWrapper class="flex !justify-end !mb-3">
                <GameSettingsButtonLiveChat
                    v-if="useUserStore().isLoggedIn"
                    v-model="isOpenLiveChat"
                />
                <GameSettingsButtonAnimate v-model="settingStore.animation" />
                <GameSettingsButtonFast v-model="game.cryptos.fast" />
                <GameSettingsButtonFairness v-model="isOpenFairness" />
                <GameSettingsButtonRule v-model="isOpenRule" name="cryptos" />
                <GameSettingsButtonVocie
                    v-model="settingStore.inhouseGames.sound.on"
                    v-model:voice-model-value="settingStore.inhouseGames.sound.music"
                    :saveAfterChange="true"
                >
                </GameSettingsButtonVocie>
            </GameSettingsDiceTabsWrapper>
        </ClientOnly>

        <div class="flex flex-col">
            <!-- Board -->
            <div
                class="p-[25px] relative grid rounded-2xl board-container"
                :style="{
                    backgroundImage: `url(${Background})`,
                    backgroundSize: '100% 100%',
                    backgroundRepeat: 'no-repeat',
                }"
            >
                <Coefficients
                    :roll_number="coefficients.roll_number"
                    :amount="oForm.amount"
                    :configs="configs"
                    :level="level"
                    :frequencies="frequencies"
                />

                <CryptosBoard
                    :roll_number="coefficients.roll_number"
                    :level="level"
                    :frequencies="frequencies"
                    :animate-enable="settingStore.animation"
                />
            </div>

            <ClientOnly>
                <CasinoBetOptionSection
                    class=""
                    :betTookit="betTookit"
                    :auto-bet-tool-kit="autoBetToolKit"
                    v-model:bet-amount-modal-value="oForm.amount"
                    v-model:is-submiting="oForm.isSubmiting"
                    :sound="settingStore.globalSounds"
                    :isAuto="game.cryptos.auto.default"
                    :bet-submit="betSubmit"
                    :is-fast="game.cryptos.fast"
                    @change-mode="toChangeMode"
                />
                <CasinoAutoSection
                    v-model:amount="oForm.amount"
                    :ref="(el) => (oAutoDialog = el)"
                    :is-fast="game.cryptos.fast"
                    :is-submiting="oForm.isSubmiting"
                    :is-auto="game.cryptos.auto.default"
                />
            </ClientOnly>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
import Background from '~/pages/casino/cryptosGame/img/cryptos-bg.jpg';
import { useGameSocket } from '~/socket/game';
import useSettingsStore from '~/stores/settings';
import { useUserStore } from '~/stores/user';
import { useBettingToolkit } from '~/common/js/bettingToolkit';
import { useAutoBetToolKit } from '~/common/js/bettingToolkit/auto';
import { useJoinStateStore } from '@/stores/cansino/joinState';
import useTranslation from '~/i18n/useTransLation';
import cryprosWinSound from '~/pages/casino/cryptosGame/mp3/crypros-win.mp3';
import matchSound from '~/pages/casino/cryptosGame/mp3/match.mp3';
import KenoResult from '~/pages/casino/cryptosGame/mp3/keno-result.mp3';
import cryptosBgm from '~/assets/mp3/game_general_bgm.mp3';
import PlaceBet from '~/assets/mp3/place-bet.mp3';
import Coefficients from './_components/Coefficients.vue';
import CryptosBoard from './_components/CryptosBoard.vue';
import { configs } from './datas';

const t = useTranslation();
const $t = useNuxtApp().$i18n.t;
const { EMIT_CRYPTOS_BET } = useGameSocket();
const isOpenFairness = ref(false);
const isOpenRule = ref(false);
const isOpenLiveChat = ref(false);
const coefficients = reactive({
    roll_number: [9, 9, 9, 9, 9],
    // Black magic... Don't even try to understand :)
    levels: {
        0: 0,
        2: 1,
        22: 2,
        3: 3,
        32: 4,
        4: 5,
        5: 6,
    },
});

const settingStore = useSettingsStore();
const { game } = storeToRefs(settingStore);

const oForm = reactive({
    amount: 0,
    isSubmiting: false,
});
const betTookit = useBettingToolkit(oForm);

// ↓ 自动模式输入面板“窗口化改造” 添加的变量与方法：
const oAutoDialog = ref(null);
const toChangeMode = (isAuto) => {
    settingStore.game.cryptos.auto.default = !!isAuto;
    if (isAuto) {
        oAutoDialog.value.switchAutoDialogVisible(true);
    }
};

const oMusicLib = {
    bg: {
        src: cryptosBgm,
        loop: true,
        isBGM: true, // true-false 表示音量取自 globalVolume, globalSoundEffects
    },
    win: {
        src: cryprosWinSound,
    },
    cryprosWin: {
        src: cryprosWinSound,
    },
    match: {
        src: matchSound,
    },
    kenoResult: {
        src: KenoResult,
    },
    placeBet: {
        src: PlaceBet,
    },
};
let musicControler = null;

/**
 * 下注接口 
 */
const betSubmit = async () => {
    coefficients.roll_number = [9, 9, 9, 9, 9];
    musicControler?.oAudio.placeBet.toPlay();
    const ts = useUserStore().queueStart(); // 防止余额立即变化
    const isAuto = Boolean(settingStore.game.cryptos.auto.default); 
    if (!isAuto) {
        useJoinStateStore().gameRunning = true;
    }
    await Sleep(100);
    const oParams = {
        amount: oForm.amount.beFixed(2),
        isFast: game.value.cryptos.fast,
        frontID: new Date().getTime(),
    };
    // if (import.meta.env.DEV_MODE && isAuto && Math.random() <= 0.2){ 
    //     oParams.amount = '0'; 
    // }
    const response = await EMIT_CRYPTOS_BET(oParams);
    if(!response || response.error || response.code == 3308){
        useUserStore().queueDelete(ts);
        isAuto || showNegativeNotify(response?.error);
        if (isAuto) return response; // 自动模式需要返回原因 
        return false;
    }
    if (game.value.cryptos.fast) {
        // ↓ 暂时不明为什么有这个无意义的赋值 
        coefficients.roll_number = [0, 1, 3, 7, 7];
        coefficients.roll_number = response.roll_number.map((item) => {
            return Number(item);
        });
        musicControler?.oAudio.kenoResult.toPlay();
        if (isAuto){
            // 防止自动模式每次下注间隔太小
            await Sleep(430);
        }
    } else {
        for (let i = 0; i < response.roll_number.length; ++i) {
            await Sleep(300);
            const number = Number(response.roll_number[i]);
            coefficients.roll_number.splice(i, 1, number);
            const inArray = coefficients.roll_number.filter((item) => item === number).length > 1;
            // 结果展示完毕后，如果下注结果大于1则播放win sound
            if (i === response.roll_number.length - 1 && response.winAmount > 1) {
                musicControler?.oAudio.cryprosWin?.toPlay();
                console.log('win', musicControler?.oAudio.cryprosWin?.duration());
            } else {
                if (musicControler?.oAudio[inArray ? 'match' : 'kenoResult']?.playing()){
                    musicControler?.oAudio[inArray ? 'match' : 'kenoResult']?.stop();
                }
                musicControler?.oAudio[inArray ? 'match' : 'kenoResult']?.toPlay();
            }
        }
    }
    // await Sleep(300);
    setTimeout(() => {
        useUserStore().queueForward(ts); // 显示余额变化
        useJoinStateStore().showDateInQueue(); // 插入游戏结果并播放动画
    }, game.value.cryptos.fast ? 500 : 0);
    if (!isAuto) {
        useJoinStateStore().gameRunning = false;
    }

    return response;
};

const autoBetToolKit = useAutoBetToolKit(oForm, betSubmit);

const frequencies = computed(() => {
    const array = coefficients.roll_number.reduce((a, v) => {
        if (v !== 9) {
            a[v] = a[v] ? a[v] + 1 : 1;
        }
        return a;
    }, {});

    for (const element in array) {
        if (array[element] === 1) {
            delete array[element];
        }
    }
    return array;
});

const level = computed(() => {
    const array = Object.values(frequencies.value).sort((a: number, b: number) => b - a);
    const level = array.join('') || '0';
    return coefficients.levels[level];
});
watch(
    () => settingStore.inhouseGames.sound.on,
    (newVal) => {
        if (newVal && !musicControler && import.meta.client) {
            musicControler = new MusicControler(oMusicLib as any);
        }
    },
    {
        immediate: true,
    },
);
onMounted(() => {
    onUnmounted(() => {
        musicControler?.toDestory();
    });
});
</script>

<style lang="scss" scoped>
.cryptosGame {
    line-height: normal;
}
.board-container {
    display: flex;
    flex-direction: column;
    padding: 25px;
    @media (max-width: 620px) {
        padding: 16px;
    }
}
</style>
